// Pure geometry for the FCD "motion" mode: stripes perpendicular to a driving
// heading that travel along it, at a pace set by the FCD speed. Kept free of OpenLayers imports so it can be
// unit-tested with `node --test`.

// FCD direction ids 1..12 are 30° heading buckets clockwise from north, matching
// the layout of the DirectionalHeatmapControl wheel (1 = N, 4 = E, 7 = S, 10 = W).
export function directionHeadingRadians(direction) {
  return ((direction - 1) * Math.PI) / 6;
}

// Projection onto the on-screen driving direction.
//   T        OL coordinateToPixelTransform [a, b, c, d, e, f] (map -> CSS px;
//            carries view rotation and the y flip)
//   heading  driving heading in radians, clockwise from north
// Returns the screen unit vector `dir` and `kx`, `ky` such that
// `kx * X + ky * Y` is the CSS-pixel distance along `dir` of map coordinate
// (X, Y), measured from the map origin. Measuring from the origin (not the
// viewport) keeps the stripes attached to the roads while panning.
export function stripeAxis(T, heading) {
  const ux = Math.sin(heading);
  const uy = Math.cos(heading);
  const vx = T[0] * ux + T[2] * uy;
  const vy = T[1] * ux + T[3] * uy;
  const len = Math.hypot(vx, vy);
  const dx = vx / len;
  const dy = vy / len;
  return {
    dir: [dx, dy],
    kx: T[0] * dx + T[1] * dy,
    ky: T[2] * dx + T[3] * dy,
  };
}

export function wrap(value, period) {
  const r = value % period;
  return r < 0 ? r + period : r;
}

// Stripe intensity (0..1) over one period of `spacing` CSS px, sampled
// `samplesPerPx` times per px: a fading tail then a bright `head` at the
// leading (+dir) end, so the stripe reads as moving forward.
export function stripeProfile(spacing, head, samplesPerPx) {
  const n = spacing * samplesPerPx;
  const profile = new Float32Array(n);
  const tailStart = spacing * 0.4;
  const headStart = spacing - head;
  for (let i = 0; i < n; i++) {
    const px = i / samplesPerPx;
    if (px >= headStart) profile[i] = 0.95;
    else if (px >= tailStart)
      profile[i] = (0.55 * (px - tailStart)) / (headStart - tailStart);
  }
  return profile;
}

// Speed bands between consecutive breakpoints plus an open-ended top band.
// Stripes animate per band rather than per pixel: a per-pixel speed would shear
// the stripe pattern more every second, a band only breaks it at band edges.
export function speedBands(breakpoints) {
  const bands = [];
  for (let i = 0; i < breakpoints.length - 1; i++) {
    const min = breakpoints[i];
    const max = breakpoints[i + 1];
    bands.push({ min, max, kmh: (min + max) / 2 });
  }
  const top = breakpoints[breakpoints.length - 1];
  bands.push({ min: top, max: Infinity, kmh: top });
  return bands;
}

export function bandIndexForSpeed(bands, kmh) {
  for (let i = 0; i < bands.length; i++) {
    if (kmh < bands[i].max) return i;
  }
  return bands.length - 1;
}

// Screen speed of the stripes: proportional to km/h (`pxPerSecAt50` at 50 km/h)
// with a floor so standing traffic still visibly crawls.
export function stripeSpeedForKmh(kmh, pxPerSecAt50, minPxPerSec) {
  return Math.max(minPxPerSec, (kmh / 50) * pxPerSecAt50);
}
